import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { MarkdownEditor } from '@/components/ui/editor';
import { useAutoResizeTextarea } from '@/components/ui/form/useAutoResizeTextArea';
import { IconButton } from '@/components/ui/IconButton';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';
import { cn } from '@/lib/utils';

import {
  MAX_SKILL_DESCRIPTION_CHARS,
  type Skill,
  type SkillForm,
} from './plugins-query';
import { SkillTeamActions } from './team-skill-actions';
import { useSkillForm } from './use-skill-form';

const SECTION_LABEL = 'text-xs font-medium text-foreground-muted';
const TRUNCATED_NOTE =
  'This skill is too large to edit here. Ask Littlebird to edit it in chat.';
/** Only show the description count once the cap is close enough to matter */
const DESCRIPTION_COUNT_FROM = MAX_SKILL_DESCRIPTION_CHARS - 100;

interface SkillCardProps {
  /** Absent when creating a new skill */
  skill?: Skill;
  /** Read-side message for a broken skill, shown in place of its description */
  problem?: string;
  initial: SkillForm;
  isTruncated: boolean;
  /** User skills are always editable; system skills are read-only */
  editing: boolean;
  /** The existing skill a typed name would overwrite or rename onto */
  findTaken?: (name: string) => Skill | undefined;
  onSaved: (dir: string) => void;
  onTry?: () => void;
  onCopy?: () => void;
  /** Bundled files, rendered under the body */
  footer?: React.ReactNode;
}

export function SkillCard({
  skill,
  problem,
  initial,
  isTruncated,
  editing,
  findTaken,
  onSaved,
  onTry,
  onCopy,
  footer,
}: SkillCardProps) {
  const {
    form,
    bodyVersion,
    setField,
    errorFor,
    canSave,
    save,
    refine,
    canRefine,
    isRefining,
    remove,
    isDeleting,
  } = useSkillForm({
    initial,
    isTruncated,
    skill,
    findTaken,
    onSaved,
  });
  const bodyError = errorFor('body');
  const descriptionError = errorFor('description');
  const descriptionRef = useAutoResizeTextarea<HTMLTextAreaElement>(
    form.description
  );
  // Fields freeze while Littlebird rewrites them, so the result can't race a keystroke
  const canType = editing && !isRefining;

  return (
    <XStack className="items-start gap-5">
      <YStack className="no-record min-w-0 flex-1 gap-4 rounded-lg border border-border bg-surface px-5 py-4">
        <XStack className="items-start gap-3">
          {editing ? (
            <Input
              id="skill-name"
              label="Name"
              disabled={isRefining}
              value={form.name}
              onChange={(event) => setField('name', event.target.value)}
              placeholder="weekly-report"
              error={errorFor('name')}
              autoFocus={!skill}
            />
          ) : (
            <span className="flex-1 font-serif text-lg">{skill?.label}</span>
          )}
          <XStack className="shrink-0 items-center gap-0.5">
            {editing && (
              <IconButton
                icon={Icons.sparkles}
                size="sm"
                tooltip={isRefining ? 'Refining...' : 'Refine with Littlebird'}
                aria-label="Refine with Littlebird"
                onClick={refine}
                disabled={!canRefine}
                isLoading={isRefining}
              />
            )}
            {onCopy && (
              <IconButton
                icon={Icons.copy}
                size="sm"
                tooltip="Copy SKILL.md"
                aria-label="Copy SKILL.md"
                onClick={onCopy}
              />
            )}
            {skill && editing && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <IconButton
                    icon={Icons.moreHorizontal}
                    size="sm"
                    aria-label="More actions"
                  />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" fit>
                  <DropdownMenu.ItemWithConfirm
                    onConfirm={remove}
                    disabled={isDeleting}
                  >
                    Delete
                  </DropdownMenu.ItemWithConfirm>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            )}
          </XStack>
        </XStack>
        {editing && problem && (
          <p className="text-xs text-foreground-muted">{problem}</p>
        )}

        <YStack className="gap-1">
          {editing ? (
            <>
              <Label htmlFor="skill-description">When to use</Label>
              <TextArea
                id="skill-description"
                ref={descriptionRef}
                rows={1}
                disabled={isRefining}
                value={form.description}
                onChange={(event) =>
                  setField('description', event.target.value)
                }
                placeholder="Use when I ask for a summary of my week"
                error={descriptionError}
                maxLength={MAX_SKILL_DESCRIPTION_CHARS}
                className="min-h-9 resize-none border-border text-sm scrollbar-hide focus-visible:border-border"
              />
              {descriptionError && (
                <p aria-live="polite" className="text-danger-11 text-xs">
                  {descriptionError}
                </p>
              )}
              {form.description.length > DESCRIPTION_COUNT_FROM && (
                <span className="self-end text-xs text-foreground-subtle">
                  {form.description.length}/{MAX_SKILL_DESCRIPTION_CHARS}
                </span>
              )}
            </>
          ) : (
            <>
              <span className={SECTION_LABEL}>When to use</span>
              <p className="text-sm text-foreground-muted">
                {problem ?? form.description}
              </p>
            </>
          )}
        </YStack>

        <YStack className={editing ? 'gap-2' : 'gap-1'}>
          {editing ? (
            <Label htmlFor="skill-body">Instructions</Label>
          ) : (
            <span className={SECTION_LABEL}>Instructions</span>
          )}
          <div
            id="skill-body"
            className={cn(
              'rounded-lg',
              editing && 'border bg-surface',
              isRefining && 'opacity-60',
              editing && (bodyError ? 'border-danger-11' : 'border-border')
            )}
          >
            <MarkdownEditor
              id="skill-body-editor"
              autofocus={false}
              editable={canType}
              initialContent={form.body}
              syncedContent={form.body}
              syncedContentVersion={bodyVersion}
              placeholder="Look at my calendar and recent activity, then summarize my week day by day"
              placeholderOnlyWhenEmpty
              featureSet="email"
              className={cn(
                '!p-0 [&_.ProseMirror]:!text-xs.5 [&_.ProseMirror]:!leading-5 [&_h1]:!text-sm [&_h1]:!mb-2 [&_h2]:!text-xs.5 [&_h2]:!mt-3 [&_h2]:!mb-1 [&_h3]:!text-xs.5 [&_h3]:!my-1 [&_li]:!my-0 [&_li]:!pl-1 [&_ul]:!my-1 [&_ol]:!my-1 [&_p]:!my-1',
                editing &&
                  '[&_.ProseMirror]:min-h-40 [&_.ProseMirror]:px-3 [&_.ProseMirror]:py-2'
              )}
              onUpdate={(markdown) => setField('body', markdown)}
            />
          </div>
          {bodyError && (
            <p aria-live="polite" className="text-danger-11 text-xs">
              {bodyError}
            </p>
          )}
          {editing && isTruncated && (
            <p className="text-xs text-foreground-muted">{TRUNCATED_NOTE}</p>
          )}
        </YStack>

        {footer}
      </YStack>

      <YStack className="w-52 shrink-0 gap-2">
        {editing && (
          <Button size="sm" onClick={save} disabled={!canSave}>
            {skill ? 'Save' : 'Create'}
          </Button>
        )}
        {onTry && (
          <Button
            variant={editing ? 'secondary' : 'default'}
            size="sm"
            onClick={onTry}
          >
            Try in chat
          </Button>
        )}
        {skill && <SkillTeamActions skill={skill} />}
      </YStack>
    </XStack>
  );
}
